import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNew from '@mui/icons-material/OpenInNew';
import * as Sentry from '@sentry/react';
import { useExpertStripeOnboardingLink } from '~/modules/connect/useExpertStripeOnboardingLink';
import { useToast } from '~/shared/contexts/ToastContext';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';

const PayoutOnboardingBanner = () => {
  const { showToast } = useToast();
  const { user } = useCurrentUser();
  const { getOnboardingUrl, isLoading } = useExpertStripeOnboardingLink();

  const hasStripeConnectAccount = Boolean(user?.stripeConnectAccountId);

  const handleClick = async () => {
    try {
      const url = await getOnboardingUrl();
      if (url) {
        window.location.assign(url);
        return;
      }
      showToast(
        'Could not start Stripe onboarding. Please try again.',
        'error',
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'stripe_connect_payout_banner' },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Could not start Stripe onboarding. Please try again.';
      showToast(message, 'error');
    }
  };

  const titleText = hasStripeConnectAccount
    ? 'Finish payout setup'
    : 'Set up your payout account';

  const descriptionText = hasStripeConnectAccount
    ? "Stripe still needs a bit more before payouts are ready. Continue in Stripe to finish verification (for example tax or identity details). This button opens Stripe's secure form."
    : "Connect a Stripe payout account to request retainers and get paid. You'll complete bank and tax details on Stripe's secure site.";

  const buttonLabel = hasStripeConnectAccount
    ? 'Continue in Stripe'
    : 'Set up payout account in Stripe';

  return (
    <BannerPaper variant="outlined">
      <Box>
        <TitleText variant="subtitle1">{titleText}</TitleText>
        <Typography variant="body2" color="text.secondary">
          {descriptionText}
        </Typography>
      </Box>
      <ActionButton
        type="button"
        variant="contained"
        size="medium"
        startIcon={
          isLoading ? (
            <CircularProgress size={16} color="inherit" aria-hidden />
          ) : (
            <OpenInNewIcon />
          )
        }
        disabled={isLoading}
        aria-busy={isLoading}
        onClick={() => {
          void handleClick();
        }}
      >
        {isLoading ? 'Opening Stripe…' : buttonLabel}
      </ActionButton>
    </BannerPaper>
  );
};

const BannerPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.container,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  textAlign: 'left',
}));

const TitleText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

const ActionButton = styled(Button)({
  textTransform: 'none',
}) as typeof Button;

const OpenInNewIcon = styled(OpenInNew)(({ theme }) => ({
  fontSize: theme.typography.h4.fontSize,
}));

export { PayoutOnboardingBanner };
